import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useQueryState, parseAsInteger, parseAsString } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { schoolService } from '../../../services';
import { SchoolTeacherFilters, SchoolTeacherCreateRequest, SchoolTeacherUpdateRequest } from '../types';
import { buildTeacherCreatePayload } from '../utils/buildTeacherCreatePayload';

// URL state parsers - only for fields that exist in data model
const searchParser = parseAsString.withDefault('');
const statusParser = parseAsString.withDefault('');
const pageParser = parseAsInteger.withDefault(1);
const perPageParser = parseAsInteger.withDefault(10);

const useSchoolTeacher = (schoolId?: string) => {
  const queryClient = useQueryClient();

  // URL state management với nuqs - only for existing fields
  const [search, setSearch] = useQueryState('search', searchParser);
  const [status, setStatus] = useQueryState('status', statusParser);
  const [page, setPage] = useQueryState('page', pageParser);
  const [per_page, setPerPage] = useQueryState('per_page', perPageParser);

  // Tạo filters object từ URL state - only existing fields
  const filters = useMemo(() => ({
    search: search || undefined,
    status: status || undefined,
  }), [search, status]);

  // Query key cho caching
  const queryKey = ['school-teachers', schoolId, filters, page, per_page];

  // Fetch teachers với react-query
  const {
    data: teachersResponse,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      console.log('🔍 Fetching school teachers...', { schoolId, search });
      try {
        const result = await schoolService.getSchoolTeachers(schoolId, search || undefined);
        console.log('✅ School teachers fetched successfully:', result);
        return result;
      } catch (error) {
        console.error('❌ Error fetching school teachers:', error);
        throw error;
      }
    },
    enabled: true, // Always enable the query
    staleTime: 5 * 60 * 1000, // 5 phút
    gcTime: 10 * 60 * 1000, // 10 phút
  });

  // All filters are now handled by server, no need for local filtering
  const teachers = useMemo(() => {
    return teachersResponse?.data || [];
  }, [teachersResponse?.data]);

  const createTeacherMutation = useMutation({
    mutationFn: (data: SchoolTeacherCreateRequest) => {
      const { userService } = require('../../../services');
      return userService.createUser(buildTeacherCreatePayload(data));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-teachers'] });
    },
  });

  // Update teacher mutation
  const updateTeacherMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: SchoolTeacherUpdateRequest }) => {
      const { userService } = require('../../../services');
      return userService.updateUser(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-teachers'] });
    },
  });

  // Delete teacher mutation
  const deleteTeacherMutation = useMutation({
    mutationFn: (id: string) => {
      const { userService } = require('../../../services');
      return userService.deleteUser(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-teachers'] });
    },
  });

  // Toggle teacher status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => {
      const { userService } = require('../../../services');
      return userService.updateUser(id, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-teachers'] });
    },
  });

  // Filter và pagination handlers - only for existing fields
  const updateFilters = useCallback((newFilters: Partial<SchoolTeacherFilters>) => {
    if (newFilters.search !== undefined) setSearch(newFilters.search || null);
    if (newFilters.status !== undefined) setStatus(newFilters.status || null);
    setPage(1);
  }, [setSearch, setStatus, setPage]);

  const updatePagination = useCallback((newPage: number, newPerPage?: number) => {
    setPage(newPage);
    if (newPerPage) setPerPage(newPerPage);
  }, [setPage, setPerPage]);

  const resetFilters = useCallback(() => {
    setSearch(null);
    setStatus(null);
    setPage(1);
  }, [setSearch, setStatus, setPage]);

  // Clear specific filter
  const clearFilter = useCallback((filterKey: keyof SchoolTeacherFilters) => {
    switch (filterKey) {
      case 'search':
        setSearch(null);
        break;
      case 'status':
        setStatus(null);
        break;
    }
    setPage(1);
  }, [setSearch, setStatus]);

  return {
    // Data
    teachers: teachers,
    pagination: {
      total: teachers.length,
      current_page: page,
      per_page,
      total_pages: Math.ceil(teachers.length / per_page)
    },
    
    // Loading states
    isLoading,
    isFetching,
    isError,
    error,

    // Current filters và pagination
    filters,
    page,
    per_page,

    // Actions
    refetch,
    updateFilters,
    updatePagination,
    resetFilters,
    clearFilter,

    // Mutations
    createTeacher: createTeacherMutation.mutate,
    updateTeacher: updateTeacherMutation.mutate,
    deleteTeacher: deleteTeacherMutation.mutate,
    toggleTeacherStatus: toggleStatusMutation.mutate,

    // Mutation states
    isCreating: createTeacherMutation.isPending,
    isUpdating: updateTeacherMutation.isPending,
    isDeleting: deleteTeacherMutation.isPending,
    isTogglingStatus: toggleStatusMutation.isPending,

    // Mutation objects for advanced handling
    createTeacherMutation,
    updateTeacherMutation,
    deleteTeacherMutation,
    toggleStatusMutation,
  };
};

export default useSchoolTeacher;
